import React from 'react';
import { Plus, Search, X } from 'lucide-react';
import { validateBroker } from '../lib/validators';

const Brokers = ({ brokers, transactions, onAddBroker, onUpdateBroker }) => {
  const [showModal, setShowModal] = React.useState(false);
  const [newBroker, setNewBroker] = React.useState({ name: '', phone: '', commissionRate: 0 });
  const [editModal, setEditModal] = React.useState({ show: false, broker: null });
  const newErrors = React.useMemo(() => validateBroker(newBroker), [newBroker]);
  const editErrors = React.useMemo(() => validateBroker(editModal?.broker || {}), [editModal?.broker]);

  const normalizeRateToFraction = (rate) => {
    const num = Number(rate || 0);
    if (!Number.isFinite(num)) return 0;
    return num > 1 ? num / 100 : num; // 2.5 -> 0.025, 0.025 -> 0.025
  };

  const toPercentNumber = (rate) => {
    const frac = normalizeRateToFraction(rate);
    return frac * 100; // 0.025 -> 2.5
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'commissionRate') {
      const percent = parseFloat(value || 0);
      const fraction = Number.isFinite(percent) ? percent / 100 : 0;
      setNewBroker((prev) => ({ ...prev, commissionRate: fraction }));
    } else {
      setNewBroker((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmitNew = (e) => {
    e.preventDefault();
    if (Array.isArray(newErrors) && newErrors.length > 0) return;
    if (onAddBroker) {
      onAddBroker({ ...newBroker });
    } else {
      console.log('Nuevo Broker:', newBroker);
    }
    setShowModal(false);
    setNewBroker({ name: '', phone: '', commissionRate: 0 });
  };
  // Calcular comisiones por broker
  const brokersWithStats = brokers.map(broker => {
    const brokerTransactions = transactions.filter(t => String(t.brokerId) === String(broker.id));
    const baseForCommission = (t) => Number(t.salePrice ?? t.amount ?? 0);
    const rateFraction = normalizeRateToFraction(broker.commissionRate);
    const totalCommission = brokerTransactions.reduce((sum, t) => sum + baseForCommission(t) * rateFraction, 0);
    const transactionCount = brokerTransactions.length;
    
    return {
      ...broker,
      rateFraction,
      totalCommission,
      transactionCount
    };
  });

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Brokers</h1>
        <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Broker
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 mb-6 neon-border">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-400" />
          <input 
            type="text" 
            placeholder="Buscar brokers..." 
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

  <div className="bg-white dark:bg-gray-800 rounded-xl  border border-gray-200 dark:border-gray-700 overflow-hidden neon-border">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Broker</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Contacto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tasa Comisión</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Transacciones</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Comisión Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {brokersWithStats.map((broker) => (
              <tr key={broker.id} className="hover:bg-gray-50 hover:dark:bg-gray-700/60">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{broker.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500 dark:text-gray-300">{broker.phone}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{toPercentNumber(broker.commissionRate).toFixed(1)}%</td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{broker.transactionCount}</td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">${broker.totalCommission.toLocaleString('es-ES')}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setEditModal({ show: true, broker })} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:dark:text-blue-300 text-sm font-medium">
                      Ver / Editar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {brokersWithStats.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay brokers para mostrar. ¡Agrega uno nuevo!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editModal?.show && editModal.broker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl relative grid grid-cols-1 md:grid-cols-2 gap-6">
            <button onClick={() => setEditModal({ show: false, broker: null })} className="absolute top-3 right-3 p-2 rounded hover:bg-gray-100 hover:dark:bg-gray-700">
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <h3 className="md:col-span-2 text-xl font-semibold text-gray-900 dark:text-white mb-2">Broker - {editModal.broker.name}</h3>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (Array.isArray(editErrors) && editErrors.length > 0) return;
                if (typeof onUpdateBroker === 'function') {
                  onUpdateBroker(editModal.broker.id, editModal.broker);
                }
                setEditModal({ show: false, broker: null });
              }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editModal.broker.name || ''}
                  onChange={(e) => setEditModal(prev => ({ ...prev, broker: { ...prev.broker, name: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={editModal.broker.phone || ''}
                  onChange={(e) => setEditModal(prev => ({ ...prev, broker: { ...prev.broker, phone: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tasa comisión (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={toPercentNumber(editModal.broker.commissionRate)}
                  onChange={(e) => {
                    const percent = Number(e.target.value || 0);
                    const fraction = Number.isFinite(percent) ? percent / 100 : 0;
                    setEditModal(prev => ({ ...prev, broker: { ...prev.broker, commissionRate: fraction } }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="pt-2">
                <button type="submit" disabled={Array.isArray(editErrors) && editErrors.length > 0} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white  transition hover:bg-blue-700 disabled:opacity-50">
                  Guardar cambios
                </button>
              </div>
            </form>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Actividad de comisiones</h4>
              {transactions.filter(t => String(t.brokerId) === String(editModal.broker.id)).slice(0, 50).map((t, idx) => {
                const commission = Number(t.brokerCommission || t.broker_commission || 0);
                const paid = Boolean(t.brokerCommissionPaid ?? t.broker_commission_paid);
                return (
                  <div key={`${t.id}-${idx}`} className="p-3 border border-gray-100 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.type === 'sale' ? 'Venta' : 'Pedido'} • {t.date || 'Sin fecha'}</div>
                      <span className={`text-xs px-2 py-0.5 rounded ${paid ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                        {paid ? 'Pagada' : 'Pendiente'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-200">Comisión: ${commission.toLocaleString('es-ES')}</div>
                  </div>
                );
              })}
              {transactions.filter(t => String(t.brokerId) === String(editModal.broker.id)).length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">Sin actividad de comisiones para este broker.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Nuevo Broker Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md relative">
            <button 
              onClick={() => setShowModal(false)} 
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Nuevo Broker</h2>
            <form onSubmit={handleSubmitNew} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Nombre</label>
                <input 
                  name="name" 
                  value={newBroker.name} 
                  onChange={handleInputChange} 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Teléfono</label>
                <input 
                  name="phone" 
                  value={newBroker.phone} 
                  onChange={handleInputChange} 
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Tasa comisión (%)</label>
                <input 
                  name="commissionRate" 
                  type="number" 
                  step="0.01" 
                  value={toPercentNumber(newBroker.commissionRate)} 
                  onChange={handleInputChange} 
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={Array.isArray(newErrors) && newErrors.length > 0}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Agregar Broker
                </button>
                {Array.isArray(newErrors) && newErrors.length > 0 && (
                  <div className="text-xs text-amber-700 dark:text-amber-300">{newErrors[0]}</div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brokers;

// Edit / Activity Modal
// We append it below to keep component together
// Render conditionally inside the component return above if needed



// Módulo de gestión de sucursales
